import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { hasHistoryView, canListenToRecording as canListenToRecordingRow } from '../../../helpers/employeePermissions'
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Rating,
  IconButton,
  Tooltip,
} from '@mui/material'
import ScheduleIcon from '@mui/icons-material/Schedule'
import CallReceivedIcon from '@mui/icons-material/CallReceived'
import CallMadeIcon from '@mui/icons-material/CallMade'
import CategoryIcon from '@mui/icons-material/Category'
import StarIcon from '@mui/icons-material/Star'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import CallMergeIcon from '@mui/icons-material/CallMerge'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import { operatorReportStyles as opStyles, employeeTableStyles as empStyles, greenTheme, employeeTableStyles } from '../../../helpers/style'
import { CALL_DIRECTIONS } from '../../../helpers/demo'
import { normalizeDisposition, normalizeDirection } from '../../../helpers/demo'
import { shortFunction, history as historyTranslate } from '../../../translate'
import { REACT_APP_API_URL } from '../../../config'
import AvTimerIcon from '@mui/icons-material/AvTimer';
import { toast } from 'react-toastify'
import moment from 'moment'
import AudioPlayerBar from '../../../components/AudioPlayerBar'

const { REACT_APP_SERVER } = process.env
const AUDIO_KEY = 'akNwNXN1U2k4bzdEQ09QU29KMUpYbXFiQTV5c3FmUm1MOUNheW5qWlYwOD0='

const headerCells = [
  { icon: ScheduleIcon, labelKey: 'dateTime' },
  { icon: CallReceivedIcon, labelKey: 'from' },
  { icon: CallMadeIcon, labelKey: 'to' },
  { icon: AvTimerIcon, labelKey: 'duration' },
  { icon: CategoryIcon, labelKey: 'type' },
  { icon: StarIcon, labelKey: 'rating', shortLabelKey: 'ratingShort' },
  { icon: PlayCircleOutlineIcon, labelKey: 'recording', shortLabelKey: 'recordingShort' },
]

const cellFontSize = '0.75rem'
const directionIcons = {
  incoming: CallReceivedIcon,
  outgoing: CallMadeIcon,
  local: LocalPhoneIcon,
  transferred: CallMergeIcon,
  auto_calls: SmartToyIcon,
}

export default function ReportHistoryTable({
  calls,
  operator,
  opTr,
  langIndex,
  getCallTypeLabel,
  getCallTypeStyle,
  formatDate,
  formatDuration,

}) {
  const employeeData = useSelector((state) => state.users.employeeData)
  const showHistoryLink = hasHistoryView(employeeData)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [playingId, setPlayingId] = useState(null)
  const [currentTrack, setCurrentTrack] = useState(null)
  const [audioDuration, setAudioDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [volume, setVolume] = useState(1)
  const audioRef = useRef(null)

  const audioBaseUrl = REACT_APP_API_URL + ((REACT_APP_SERVER === 'Ucom' || REACT_APP_SERVER === 'V-office') ? '/rest' : '')

  const maxPage = Math.max(0, Math.ceil(calls.length / rowsPerPage) - 1)
  const safePage = Math.min(page, maxPage)

  useEffect(() => {
    setPage((prev) => Math.min(prev, maxPage))
  }, [calls.length, rowsPerPage, maxPage])

  const paginatedCalls = useMemo(() => {
    const start = safePage * rowsPerPage
    return calls.slice(start, start + rowsPerPage)
  }, [calls, safePage, rowsPerPage])

  const handleChangePage = useCallback((_, newPage) => setPage(newPage), [])
  const handleChangeRowsPerPage = useCallback((e) => {
    setRowsPerPage(parseInt(e.target.value, 10))
    setPage(0)
  }, [])

  const formatDateOnly = useCallback((isoOrStamp) => {
    if (!isoOrStamp) return ''
    const m = typeof isoOrStamp === 'number' ? moment.unix(isoOrStamp) : moment(isoOrStamp)
    if (!m.isValid()) return ''
    return m.subtract(4, 'hours').format('DD-MM-YYYY HH:mm')
  }, [])

  const formatTime = useCallback((sec) => {
    if (sec == null || isNaN(sec)) return '0:00'
    const m = Math.floor(sec / 60)
    const s = Math.floor(sec % 60)
    return `${m}:${String(s).padStart(2, '0')}`
  }, [])

  const formatFileSize = useCallback(() => '', [])
  const formatFileDate = useCallback((d) => (d ? formatDateOnly(d) : ''), [formatDateOnly])

  const getDisplayForSip = useCallback((val) => {
    if (!val) return ''
    const str = String(val).trim()
    const buddy = String(operator?.buddy || '').trim()
    if (!buddy) return str
    const parts = str.split(/[-/]/).map((p) => String(p).trim()).filter(Boolean)
    if (parts.includes(buddy) || str === buddy) return operator?.email || str
    return str
  }, [operator?.buddy, operator?.email])


  const progress = useMemo(() => {
    if (!audioDuration || audioDuration <= 0) return 0
    return Math.min(100, (currentTime / audioDuration) * 100)
  }, [currentTime, audioDuration])

  const playerLabel = useMemo(() => {
    if (!currentTrack) return ''
    const from = getDisplayForSip(currentTrack.extension || currentTrack.in_num) || ''
    const to = getDisplayForSip(currentTrack.destination) || ''
    return `${from} → ${to}`
  }, [currentTrack, getDisplayForSip])

  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    const onTimeUpdate = () => setCurrentTime(el.currentTime)
    const onLoadedMetadata = () => setAudioDuration(el.duration)
    const onEnded = () => {
      setPlayingId(null)
      setCurrentTime(0)
    }
    const onError = () => {
      toast.error(opTr.recordingNotAvailable?.[langIndex] ?? 'Recording not available')
      setPlayingId(null)
      setCurrentTrack(null)
    }
    el.addEventListener('timeupdate', onTimeUpdate)
    el.addEventListener('loadedmetadata', onLoadedMetadata)
    el.addEventListener('ended', onEnded)
    el.addEventListener('error', onError)
    return () => {
      el.removeEventListener('timeupdate', onTimeUpdate)
      el.removeEventListener('loadedmetadata', onLoadedMetadata)
      el.removeEventListener('ended', onEnded)
      el.removeEventListener('error', onError)
    }
  }, [langIndex, opTr.recordingNotAvailable])

  useEffect(() => {
    const el = audioRef.current
    if (el) el.volume = volume
  }, [volume])

  const canListenToRecording = useCallback(
    (row) => canListenToRecordingRow(employeeData, row),
    [employeeData]
  )

  const handlePlayPauseRecording = useCallback(
    (row) => {
      if (!canListenToRecording(row)) return
      if (!row?.uniqueid) return
      const el = audioRef.current
      if (!el) return

      if (playingId === row.uniqueid) {
        el.pause()
        setPlayingId(null)
        return
      }

      const src = `${audioBaseUrl}/history/audio?file=${encodeURIComponent(row.uniqueid)}&key=${AUDIO_KEY}`
      el.src = src
      el.play().catch(() => {
        toast.error(opTr.recordingNotAvailable?.[langIndex] ?? 'Recording not available')
      })
      setCurrentTrack(row)
      setPlayingId(row.uniqueid)
      setCurrentTime(0)
    },
    [audioBaseUrl, canListenToRecording, langIndex, opTr.recordingNotAvailable, playingId]
  )

  const handlePlayerPlayPause = useCallback(() => {
    const el = audioRef.current
    if (!el || !currentTrack) return
    if (playingId) {
      el.pause()
      setPlayingId(null)
    } else {
      el.play().catch(() => toast.error(opTr.recordingNotAvailable?.[langIndex] ?? 'Recording not available'))
      setPlayingId(currentTrack.uniqueid)
    }
  }, [currentTrack, langIndex, opTr.recordingNotAvailable, playingId])

  const handleSeek = useCallback((_, value) => {
    const el = audioRef.current
    if (!el || !audioDuration) return
    const t = (Number(value) / 100) * audioDuration
    el.currentTime = t
    setCurrentTime(t)
  }, [audioDuration])

  const handleDownload = useCallback(
    (track) => {
      if (!track?.uniqueid) return
      const src = `${audioBaseUrl}/history/audio?file=${encodeURIComponent(track.uniqueid)}&key=${AUDIO_KEY}`
      window.open(src, '_blank', 'noopener')
    },
    [audioBaseUrl]
  )

  return (
    <div className="tableParent">
      <Box id="section-numbers" sx={{...opStyles.sectionBox, margin:0}}>
        <audio ref={audioRef} style={{ display: 'none' }} />
        <Box sx={{ ...empStyles.headerBox, width: '100%', justifyContent: 'space-between', alignItems: 'center', padding: "24px", margin: 0 }}>
          <Typography sx={empStyles.title} variant="h5" component="h1">
            {opTr.history?.[langIndex] ?? historyTranslate.history1?.[langIndex] ?? 'History'}
          </Typography>
          {showHistoryLink ? (
          <Link
            to="/history"
            style={{ fontSize: 14, color: 'inherit', textDecoration: 'underline', fontWeight: 500 }}
          >
            {opTr.moreDetailedHistory?.[langIndex] ?? 'More Detailed history'}
          </Link>
          ) : null}
        </Box>
        {currentTrack && (
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 3,
              backgroundColor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <AudioPlayerBar
              onPrev={() => {}}
              onNext={() => {}}
              onPlayPause={handlePlayerPlayPause}
              onSeek={handleSeek}
              onDownload={handleDownload}
              currentTrack={currentTrack}
              playingId={playingId}
              progress={progress}
              duration={audioDuration}
              currentTime={currentTime}
              volume={volume}
              onVolumeChange={setVolume}
              playerLabel={playerLabel}
              canGoPrev={false}
              canGoNext={false}
              formatTime={formatTime}
              formatFileSize={formatFileSize}
              formatFileDate={formatFileDate}
              langIndex={langIndex}
              paperSx={{ m: 0 }}
            />
          </Box>
        )}
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            ...opStyles.tableContainer,
            width: '100%',
            overflow: 'visible',
            boxShadow: 0,
            borderRadius: 0,
            minHeight: 240,
          }}
        >
          <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', '& .MuiTableCell-root': { fontSize: cellFontSize, width: 100, minWidth: 100 }, '& .MuiTableHead .MuiTableCell-root': { fontSize: '0.7rem' } }}>
            <TableHead>
              <TableRow sx={opStyles.tableHeadRow}>
                {headerCells.map(({ icon: Icon, labelKey, shortLabelKey }) => {
                  const displayLabel = (shortLabelKey ? opTr[shortLabelKey] : opTr[labelKey])?.[langIndex] ?? opTr[labelKey]?.[langIndex]
                  const fullLabel = labelKey === 'rating' ? (shortFunction.rate?.[langIndex] ?? opTr.rating?.[langIndex]) : opTr[labelKey]?.[langIndex]
                  const showTooltip = shortLabelKey && fullLabel && displayLabel !== fullLabel
                  return (
                    <TableCell key={labelKey} sx={{ ...opStyles.tableHeadCell, fontSize: cellFontSize }} align={labelKey === 'rating' ? 'center' : undefined}>
                      <Box sx={opStyles.tableHeadCellBox} component={showTooltip ? 'span' : undefined}>
                        {showTooltip ? (
                          <Tooltip title={fullLabel}>
                            <Box component="span" sx={{ display: 'flex', alignItems: 'center', justifyContent: labelKey === 'rating' ? 'center' : undefined, gap: 0.75 }}>
                              <Icon sx={opStyles.tableHeadIcon} />
                              {displayLabel}
                            </Box>
                          </Tooltip>
                        ) : (
                          <>
                            <Icon sx={opStyles.tableHeadIcon} />
                            {displayLabel}
                          </>
                        )}
                      </Box>
                    </TableCell>
                  )
                })}
              </TableRow>
            </TableHead>
            <TableBody sx={{ minHeight: 240 }}>
              {calls.length === 0 ? (
                null
              ) : (
                paginatedCalls.map((row, idx) => {
                  const typeKey = normalizeDisposition(row.disposition)
                  const directionKey = normalizeDirection(row)
                  const style = getCallTypeStyle(typeKey)
                  const typeLabel = getCallTypeLabel(typeKey)
                  const DirectionIcon = directionIcons[directionKey] || CallReceivedIcon
                  return (
                    <TableRow key={row.uniqueid || idx} hover sx={opStyles.tableBodyRow}>
                      <TableCell sx={opStyles.tableCell} title={formatDate(row.start)}>
                        <Box sx={{ ...opStyles.tableDirectionBox, justifyContent: 'flex-start', gap: 0.5, fontSize: cellFontSize }}>
                          <DirectionIcon sx={{ fontSize: 14, color: CALL_DIRECTIONS.find((d) => d.key === directionKey)?.color || 'inherit', flexShrink: 0 }} />
                          {formatDateOnly(row.start)}
                        </Box>
                      </TableCell>
                      <TableCell sx={opStyles.tableCellMonospace} title={row.extension || row.in_num || ''}>{getDisplayForSip(row.extension || row.in_num)}</TableCell>
                      <TableCell sx={opStyles.tableCellMonospace} title={row.destination || ''}>{getDisplayForSip(row.destination)}</TableCell>
                      <TableCell sx={opStyles.tableCellDuration}>{formatDuration(row.duration)}</TableCell>
                      <TableCell sx={opStyles.tableCell}>
                        <Tooltip title={row.disposition ? typeLabel : ''}>
                          <Chip
                            size="small"
                            label={row.disposition ? (typeLabel.length > 7 ? `${typeLabel.slice(0, 7)}…` : typeLabel) : ''}
                            sx={{ ...opStyles.tableChip, fontSize: '0.7rem', backgroundColor: style.bg, color: style.color, border: `1px solid ${style.border}` }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={opStyles.tableCell} align="center">
                        {row.rate != null && row.rate > 0 ? (
                          <Rating size="small" value={+row.rate} readOnly max={5} precision={0.5} sx={opStyles.ratingStars} />
                        ) : (
                          <Typography variant="body2" sx={{ fontSize: cellFontSize }} color="text.secondary"></Typography>
                        )}
                      </TableCell>
                      <TableCell sx={opStyles.tableCell} align="center">
                        {(() => {
                          const canPlay = canListenToRecording(row)
                          const playTooltip = !canPlay
                            ? (opTr.recordingNoPermission?.[langIndex] ?? 'No permission to listen to recordings')
                            : playingId === row.uniqueid
                              ? (opTr.pauseRecording?.[langIndex] ?? 'Pause')
                              : (opTr.playRecording?.[langIndex] ?? 'Play recording')
                          return (
                            <Tooltip title={playTooltip}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handlePlayPauseRecording(row)}
                                  disabled={!canPlay}
                                  aria-label={playingId === row.uniqueid ? 'Pause' : 'Play'}
                                  sx={{ color: canPlay ? (greenTheme?.primary || 'primary.main') : '#ccc' }}
                                >
                                  {playingId === row.uniqueid ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )
                        })()}
                      </TableCell>
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
          {calls.length > 0 && (
            <TablePagination
              component="div"
              sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: cellFontSize } }}
              count={calls.length}
              page={safePage}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage={opTr.rowsPerPage?.[langIndex] ?? 'Rows per page:'}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} ${opTr.of?.[langIndex] ?? 'of'} ${count}`
              }
            />
          )}
          {calls.length === 0 && (
            <Box sx={employeeTableStyles.emptyStateBox}>
              <Typography variant="body1" color="text.secondary">
                {opTr.noCallsTable?.[langIndex]}
              </Typography>
            </Box>
          )}
        </TableContainer>
      </Box>

    </div>

  )
}
